import { ApolloError, useMutation } from '@apollo/client'
import { Button } from 'components/v2/Button'
import Heading from 'components/v2/Heading'
import Modal from 'components/v2/Modal'
import {
  ASSIGN_BUSINESS_CUSTOMERS_KREDIT_CATEGORY,
  CHANGE_BUSINESS_BULK_CUSTOMER_KREDITS_AMOUNT
} from 'features/BusinessCustomer/mutations-legacy'
import { ErrorMessage } from 'components/v2/ErrorMessage'

import { ReactElement } from 'react'
import { FormattedMessage } from 'react-intl'
import { GTBusinessCustomer } from 'gql/types.generated'
import { Formik } from 'formik'
import { InputCurrency } from 'components/InputCurrency'
import { Label } from 'components/v2/Label'
import SelectKreditsCategory from 'features/Kredits/components/SelectKreditsCategory'
import { Row } from 'components/v2/Row'
import { Column } from 'components/v2/Column'
import { isEmpty } from 'lodash'
import { getUnprocessableErrors } from '@10xlabs/utils'
import { GET_BUSINESS_CUSTOMERS_LIST } from 'features/BusinessCustomer/queries-legacy'
import { sanitizePrice } from 'utils/math'

interface IBulkAssignKreditsModalProps {
  selectedCustomers: GTBusinessCustomer[]
  closeModal: () => void
  onSuccess: () => void
  businessClientId: string
}
export const BulkAssignKreditsModal = ({
  selectedCustomers,
  closeModal,
  onSuccess,
  businessClientId
}: IBulkAssignKreditsModalProps): ReactElement => {
  const [assignBusinessCustomersKreditCategory, { loading: loadingAssigningCategory, error: errorAssigningCategory }] =
    useMutation(ASSIGN_BUSINESS_CUSTOMERS_KREDIT_CATEGORY, {
      onCompleted: (): void => {
        onSuccess()
      },
      onError(): void {
        //DO NOTHING
      },
      refetchQueries: [GET_BUSINESS_CUSTOMERS_LIST]
    })

  const [changeBusinessClientCustomersKreditsAmount, { loading: loadingAmount, error: errorAmount }] = useMutation(
    CHANGE_BUSINESS_BULK_CUSTOMER_KREDITS_AMOUNT,
    {
      onCompleted: (): void => {
        onSuccess()
      },
      onError(): void {
        //DO NOTHING
      },
      refetchQueries: [GET_BUSINESS_CUSTOMERS_LIST]
    }
  )

  const handleSubmit = (formValues: IBulkAssignKreditsFormValues): void => {
    selectedCustomers

    if (!isEmpty(formValues.amount)) {
      changeBusinessClientCustomersKreditsAmount({
        variables: {
          input: {
            id: businessClientId,
            customerIDs: selectedCustomers.map((customer) => customer.id),
            amount: sanitizePrice(parseFloat(formValues.amount) * 100)
          }
        }
      })
    }

    if (!isEmpty(formValues.category)) {
      assignBusinessCustomersKreditCategory({
        variables: {
          input: {
            ids: selectedCustomers.map((customer) => customer.id),
            category: parseInt(formValues.category)
          }
        }
      })
    }
  }

  return (
    <Modal.Container>
      <Modal.Header>
        <Heading.H5>
          <FormattedMessage id="businessCustomer.bulkAssignKredits.title" defaultMessage="Edit customers spread" />
        </Heading.H5>
      </Modal.Header>
      <Form
        businessClientId={businessClientId}
        onSubmit={handleSubmit}
        onCancel={closeModal}
        loading={loadingAssigningCategory || loadingAmount}
        error={errorAssigningCategory || errorAmount}
      />
    </Modal.Container>
  )
}

interface IBulkAssignKreditsFormValues {
  amount: string
  category: string
}

const Form = ({
  businessClientId,
  onSubmit,
  onCancel,
  loading,
  error
}: {
  businessClientId: string
  onSubmit: (values: IBulkAssignKreditsFormValues) => void
  onCancel: () => void
  loading: boolean
  error?: ApolloError
}): ReactElement => {
  const initialValues: IBulkAssignKreditsFormValues = { amount: '', category: '' }
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values): void => {
        onSubmit(values)
      }}
    >
      {(props): ReactElement => (
        <form onSubmit={props.handleSubmit}>
          <Modal.Content>
            <Row>
              <Column>
                <Label>
                  <FormattedMessage id="kredits.actions.depositKreditsModal.category" defaultMessage="Category" />
                </Label>
                <SelectKreditsCategory
                  businessClientId={businessClientId}
                  name="category"
                  disabled={!isEmpty(props.values.amount)}
                  onChange={(e): void => {
                    props.setValues({ ...props.values, category: e.target.value, amount: '' })
                  }}
                />
              </Column>
            </Row>
            <Row>
              <Column>
                <Label>
                  <FormattedMessage id="kredits.actions.depositKreditsModal.amount" defaultMessage="Amount" />
                </Label>
                <InputCurrency
                  id="amount"
                  value={props.values.amount}
                  placeholder="$0.00"
                  onChange={(value): void => {
                    props.setValues({ ...props.values, amount: value, category: '' })
                  }}
                  onBlur={props.handleBlur}
                  touched={props.touched.amount}
                  disabled={!isEmpty(props.values.category)}
                  errors={props.errors.amount}
                  hideSuffix
                />
              </Column>
            </Row>
          </Modal.Content>
          <Modal.Footer>
            {error && (
              <Modal.ErrorMessage>
                <ErrorMessage errors={getUnprocessableErrors(error)}>
                  <FormattedMessage
                    id="kredits.actions.depositKreditsModal.error"
                    defaultMessage="An error has occurred "
                  />
                </ErrorMessage>
              </Modal.ErrorMessage>
            )}
            <Modal.Actions>
              <Button kind="cancel" type="button" onClick={onCancel}>
                <FormattedMessage id="kredits.actions.depositKreditsModal.cancel" defaultMessage="Back" />
              </Button>
              <Button type="submit" disabled={loading || !props.dirty} loading={loading}>
                <FormattedMessage id="kredits.actions.depositKreditsModal.submit" defaultMessage="Edit" />
              </Button>
            </Modal.Actions>
          </Modal.Footer>
        </form>
      )}
    </Formik>
  )
}
